import React from 'react';
import { Select, InputNumber, Text, IconButton, SelectSizes, InputSizes } from '@clockout-dev/design-elements';
import { DeleteOutlined } from '@ant-design/icons';
import RulesPanel from '../../../common/rulesPanel/RulesPanel';
import EmptyState from '../../../common/emptyState/EmptyState';

/**
 * Rows of product-type or geography rules — "<prefix> [product | states]
 * <valueLabel> [value] <valueSuffixLabel>" — shared by the Fee Structure,
 * Available Balance and Limits tabs. Each host passes its own form's row and
 * field classes so the rows match the form they sit in.
 *
 * Props:
 * - `rules` — `{ id, productType | states, [valueKey] }[]`
 * - `matchKey` — 'productType' (one product) or 'states' (one or more states)
 * - `options` — the select options (the FI's products, or the states); with
 *   none (an FI without a product catalog) the panel shows only that, no rows
 * - `prefix`, `valueLabel`, `valueSuffixLabel` — row copy
 * - `valueKey`, `inputProps` — the value field and its InputNumber bounds / affixes
 * - `onAdd`, `onRemove(id)`, `onUpdate(id, field, value)`
 * - `addLabel`, `emptyTitle`, `emptyDescription`
 * - `classNames` — `{ row, label, select, input }` from the host form's stylesheet
 */
export default function ProductOrStateRulesPanel({
    rules = [],
    matchKey,
    options = [],
    prefix,
    valueLabel,
    valueSuffixLabel,
    valueKey,
    inputProps = {},
    onAdd,
    onRemove,
    onUpdate,
    addLabel,
    emptyTitle,
    emptyDescription,
    classNames = {},
}) {
    const isStates = matchKey === 'states';

    if (!options.length) {
        return (
            <EmptyState
                title="No product catalog"
                description="This FI has no product catalog, so product type rules can't apply."
            />
        );
    }

    return (
        <RulesPanel
            onAdd={onAdd}
            addLabel={addLabel}
            emptyState={<EmptyState title={emptyTitle} description={emptyDescription} />}
        >
            {rules.map((rule) => (
                <div key={rule.id} className={classNames.row}>
                    <Text as="span" size="s" className={classNames.label}>
                        {prefix}
                    </Text>
                    <Select
                        size={SelectSizes.SMALL}
                        mode={isStates ? 'multiple' : undefined}
                        value={rule[matchKey]}
                        options={options}
                        onChange={(value) => onUpdate(rule.id, matchKey, value)}
                        placeholder={isStates ? 'Select states' : 'Select a product'}
                        optionFilterProp="label"
                        className={classNames.select}
                    />
                    <Text as="span" size="s" className={classNames.label}>
                        {valueLabel}
                    </Text>
                    <InputNumber
                        size={InputSizes.SMALL}
                        value={rule[valueKey]}
                        onChange={(value) => onUpdate(rule.id, valueKey, value)}
                        controls={false}
                        className={classNames.input}
                        {...inputProps}
                    />
                    {valueSuffixLabel ? (
                        <Text as="span" size="s" className={classNames.label}>
                            {valueSuffixLabel}
                        </Text>
                    ) : null}
                    <IconButton
                        type="button"
                        icon={<DeleteOutlined />}
                        aria-label="Remove row"
                        onClick={() => onRemove(rule.id)}
                    />
                </div>
            ))}
        </RulesPanel>
    );
}
